import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  Lening,
  LeningAanwending,
  LeningAflossingsMethode,
  LeningGeldVerstrekker,
  OmschrijvingEnBedrag
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type LeningFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  rekeningNummer: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  landCode: FormControl<string | null>;
  aanwending: FormControl<LeningAanwending | null>;
  geldVerstrekker: FormControl<LeningGeldVerstrekker | null>;
  wozNummerWaarLeningBijHoort: FormControl<string | null>;
  percentageBox1: FormControl<number | null>;
  hoofdsom0101: FormControl<number | null>;
  hoofdsom3112: FormControl<number | null>;
  rente: FormControl<number | null>;
  kosten: FormArray<OmschrijvingBedragFormGroup>,
  personeelsKorting: FormControl<number | null>;
  aanvangsDatum: FormControl<Date | null>;
  eindDatum: FormControl<Date | null>;
  oorspronkelijkeHoofdsom: FormControl<number | null>;
  rentePercentageDecimalen: FormControl<number | null>;
  aflossingsMethode: FormControl<LeningAflossingsMethode | null>;
}>;

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-eigenwoning-restschulden',
  templateUrl: './eigenwoning-restschulden.component.html',
  styleUrls: ['./eigenwoning-restschulden.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class EigenwoningRestschuldenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public dialogVisible = false;

  public formGroup = new FormGroup({
    leningen: new FormArray<LeningFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    )
      .subscribe((data) => {
        this.updateForm(data.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.restschuldEigenWoningen || []);
      });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () => this.leningen.getRawValue().map(w => ({
      rente: w.rente,
      percentageBox1: w.percentageBox1,
    }));

    this.changeSubscriptions.add(
      this.leningen.valueChanges.pipe(
        debounceTime(500),
        tap(() => this.saveForm()),
        map(() => extractRelevant()),
        distinctUntilChanged((prev, curr) =>
          JSON.stringify(prev) === JSON.stringify(curr)
        ),
        switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe()
    );
  }

  get leningen(): FormArray<LeningFormGroup> {
    return this.formGroup.controls.leningen;
  }

  addRij(): void {
    this.leningen.push(this.createFormGroup(), {emitEvent: false});
  }

  deleteRij(index: number): void {
    this.leningen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: Lening): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      rekeningNummer: new FormControl<string | null>(item?.rekeningNummer ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      landCode: new FormControl<string | null>(item?.landCode ?? null),
      aanwending: new FormControl<LeningAanwending | null>(item?.aanwending ?? null),
      geldVerstrekker: new FormControl<LeningGeldVerstrekker | null>(item?.geldVerstrekker ?? null),
      wozNummerWaarLeningBijHoort: new FormControl<string | null>(item?.wozNummerWaarLeningBijHoort ?? null),
      percentageBox1: new FormControl<number | null>(item?.percentageBox1 ?? null),
      hoofdsom0101: new FormControl<number | null>(item?.hoofdsom0101 ?? null),
      hoofdsom3112: new FormControl<number | null>(item?.hoofdsom3112 ?? null),
      rente: new FormControl<number | null>(item?.rente ?? null),
      kosten: new FormArray<OmschrijvingBedragFormGroup>(
        (item?.kosten ?? []).map(k => this.createOmschrijvingBedragFormGroup(k))
      ),
      personeelsKorting: new FormControl<number | null>(item?.personeelsKorting ?? null),
      aanvangsDatum: new FormControl<Date | null>(item?.aanvangsDatum ?? null),
      eindDatum: new FormControl<Date | null>(item?.eindDatum ?? null),
      oorspronkelijkeHoofdsom: new FormControl<number | null>(item?.oorspronkelijkeHoofdsom ?? null),
      rentePercentageDecimalen: new FormControl<number | null>(item?.rentePercentageDecimalen ?? null),
      aflossingsMethode: new FormControl<LeningAflossingsMethode | null>(item?.aflossingsMethode ?? null),
    });
  }

  private createOmschrijvingBedragFormGroup(item?: OmschrijvingEnBedrag): OmschrijvingBedragFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    });
  }

  private updateForm(data: Lening[]): void {
    const lijst = data || [];
    const formArray = this.leningen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }


  saveForm(): void {

    const leningen: Lening[] = this.formGroup.getRawValue().leningen
      .filter(item => item.rekeningNummer || item.bedrag || item.landCode || item.aanwending || item.geldVerstrekker || item.wozNummerWaarLeningBijHoort || item.percentageBox1 || item.hoofdsom0101 || item.hoofdsom3112 || item.rente || (item.kosten && item.kosten.length > 0) || item.personeelsKorting || item.aanvangsDatum || item.eindDatum || item.oorspronkelijkeHoofdsom || item.rentePercentageDecimalen || item.aflossingsMethode)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        rekeningNummer: item?.rekeningNummer!,
        bedrag: item?.bedrag!,
        landCode: item?.landCode!,
        aanwending: item?.aanwending!,
        geldVerstrekker: item?.geldVerstrekker!,
        wozNummerWaarLeningBijHoort: item?.wozNummerWaarLeningBijHoort!,
        percentageBox1: item?.percentageBox1!,
        hoofdsom0101: item?.hoofdsom0101 ?? undefined,
        hoofdsom3112: item?.hoofdsom3112 ?? undefined,
        rente: item?.rente ?? undefined,
        kosten: item?.kosten?.length
          ? item.kosten.map(k => ({
            omschrijving: k.omschrijving ?? undefined,
            bedrag: k.bedrag ?? undefined,
          }))
          : undefined,
        personeelsKorting: item?.personeelsKorting ?? undefined,
        aanvangsDatum: item?.aanvangsDatum ?? undefined,
        eindDatum: item?.eindDatum ?? undefined,
        oorspronkelijkeHoofdsom: item?.oorspronkelijkeHoofdsom ?? undefined,
        rentePercentageDecimalen: item?.rentePercentageDecimalen ?? undefined,
        aflossingsMethode: item?.aflossingsMethode ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          box1: {
            ...currentEntity.aangifte.box1,
            belastbareInkomstenUitEigenWoning: {
              ...currentEntity.aangifte.box1?.belastbareInkomstenUitEigenWoning,
              restschuldEigenWoningen: leningen
            }
          }
        },
      } as IncomeTaxEntity;
    });
  }
}
